/**
 * Phase A4 — /forgot-password
 * Enumeration-safe: always shows the same "check your inbox" success
 * state on 200, whether the email exists or not. Route takes
 * ?role=employer|employee (defaults to employer).
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { passwordForgot } from "../lib/api";
import { track } from "../lib/analytics";
import { isValidEmail } from "../lib/dataHygiene";
import BackLink from "../components/BackLink";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import "./SetPassword.css";

export default function ForgotPassword() {
    const { t, i18n } = useTranslation();
    const [params] = useSearchParams();
    const role = params.get("role") === "employee" ? "employee" : "employer";

    const [email, setEmail] = useState(params.get("email") || "");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        track("forgot_password_view", { role });
        document.title = t("auth.forgot.heading");
        inputRef.current && inputRef.current.focus();
    }, [role, t]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (submitting) return;
        if (!isValidEmail(email.trim())) {
            setErr("network"); // cheap reuse — "enter a valid email"
            return;
        }
        setErr("");
        setSubmitting(true);
        try {
            await passwordForgot({
                role, email: email.trim(),
                locale: i18n.language?.startsWith("es") ? "es" : "en",
            });
            track("forgot_password_submit", { role, outcome: "ok" });
            setSent(true);
        } catch (_) {
            // Even on error we show the success state — enumeration
            // safety trumps network-error verbosity here. Logged only.
            track("forgot_password_submit", { role, outcome: "network" });
            setSent(true);
        } finally {
            setSubmitting(false);
        }
    }

    const backTo = role === "employer" ? "/employer-signin" : "/apply";

    if (sent) {
        return (
            <section className="set-password" data-testid="forgot-password-success">
                <div className="set-password-backrow">
                    <BackLink to={backTo} testId="forgot-password-back" />
                </div>
                <div className="set-password-inner">
                    <p className="set-password-eyebrow mono">
                        {t("auth.forgot.success.eyebrow")}
                    </p>
                    <h1 className="set-password-title">
                        {t("auth.forgot.success.heading")}
                    </h1>
                    <p className="set-password-sub">
                        {t("auth.forgot.success.body")}
                    </p>
                    <div className="set-password-secondary">
                        <button
                            type="button"
                            className="set-password-secondary-link"
                            data-testid="forgot-password-try-again"
                            onClick={() => {
                                setSent(false);
                                setTimeout(() => inputRef.current?.focus(), 40);
                            }}
                        >
                            {t("auth.forgot.success.tryAgain")}
                        </button>
                        <a
                            href={CALENDLY_SUCCESS_TEAM_URL}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="set-password-secondary-link"
                            data-testid="forgot-password-contact"
                        >
                            {t("auth.signin.errors.contactUs")}
                        </a>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="set-password" data-testid="forgot-password-page">
            <div className="set-password-backrow">
                <BackLink to={backTo} testId="forgot-password-back" />
            </div>

            <div className="set-password-inner">
                <p className="set-password-eyebrow mono">{t("auth.forgot.eyebrow")}</p>
                <h1 className="set-password-title">{t("auth.forgot.heading")}</h1>
                <p className="set-password-sub">{t("auth.forgot.subhead")}</p>

                {err && (
                    <div
                        className="set-password-error-banner"
                        role="status"
                        data-testid="forgot-password-error"
                    >
                        {t("auth.forgot.errors.network")}
                    </div>
                )}

                <form
                    className="set-password-form"
                    onSubmit={handleSubmit}
                    autoComplete="on"
                    data-testid="forgot-password-form"
                    noValidate
                >
                    <label className="set-password-label mono" htmlFor="forgot-password-email">
                        {t("auth.forgot.emailLabel")}
                    </label>
                    <input
                        ref={inputRef}
                        id="forgot-password-email"
                        type="email"
                        name="email"
                        autoComplete="username"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="set-password-input"
                        data-testid="forgot-password-email"
                    />

                    <button
                        type="submit"
                        className="set-password-submit"
                        disabled={submitting || !email.trim()}
                        data-testid="forgot-password-submit"
                    >
                        {submitting
                            ? t("auth.forgot.submitting")
                            : t("auth.forgot.submit")}
                    </button>
                </form>

                <div className="set-password-secondary">
                    <a
                        href={CALENDLY_SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="set-password-secondary-link"
                        data-testid="forgot-password-contact"
                    >
                        {t("auth.signin.errors.contactUs")}
                    </a>
                </div>
            </div>
        </section>
    );
}
